import { A } from '@solidjs/router';
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
} from 'solid-js';
import { fetchFeaturedItems, type MarketplaceItem, searchMarketplace } from '../services/api';

type ViewMode = 'card' | 'table';
type PageSize = 30 | 50 | 100;

const Marketplace: Component = () => {
  // Pagination state
  const [pageSize, setPageSize] = createSignal<PageSize>(30);
  const [currentPage, setCurrentPage] = createSignal(1);

  // Filter state
  const [selectedType, setSelectedType] = createSignal<string>('');
  const [selectedCategory, setSelectedCategory] = createSignal<string>('');
  const [minRating, setMinRating] = createSignal<number | undefined>(undefined);
  const [searchQuery, setSearchQuery] = createSignal('');

  // UI state
  const [viewMode, setViewMode] = createSignal<ViewMode>('card');
  const [showFeatured, setShowFeatured] = createSignal(true);

  // Computed pagination params
  const paginationParams = createMemo(() => ({
    limit: pageSize(),
    offset: (currentPage() - 1) * pageSize(),
    type: selectedType() || undefined,
    category: selectedCategory() || undefined,
    minRating: minRating(),
    search: searchQuery() || undefined,
  }));

  // Fetch marketplace items
  const [marketplaceData, { refetch: _refetch }] = createResource(
    paginationParams,
    searchMarketplace,
  );

  // Fetch featured items
  const [featuredItems] = createResource(fetchFeaturedItems);

  // Derived values
  const items = createMemo(() => marketplaceData()?.items || []);
  const totalCount = createMemo(() => marketplaceData()?.total || 0);
  const totalPages = createMemo(() => Math.ceil(totalCount() / pageSize()));

  // Reset to page 1 when filters change
  createEffect(() => {
    selectedType();
    selectedCategory();
    minRating();
    searchQuery();
    pageSize();
    setCurrentPage(1);
  });

  const _formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedCategory('');
    setMinRating(undefined);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const hasActiveFilters = createMemo(
    () =>
      selectedType() !== '' ||
      selectedCategory() !== '' ||
      minRating() !== undefined ||
      searchQuery() !== '',
  );

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'project':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'agent':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'skill':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'artifact':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'project':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-purple-400"
          >
            <path d="M3 3v18h18" />
            <path d="m19 9-5 5-4-4-3 3" />
          </svg>
        );
      case 'agent':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-blue-400"
          >
            <path d="M12 8V4H8" />
            <rect width="16" height="12" x="4" y="8" rx="2" />
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            <path d="M15 13v2" />
            <path d="M9 13v2" />
          </svg>
        );
      case 'skill':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-cyan-400"
          >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        );
      case 'artifact':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-amber-400"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-400"
          >
            <rect width="18" height="18" x="3" y="3" rx="2" />
          </svg>
        );
    }
  };

  const getPricingBadge = (pricing?: MarketplaceItem['pricing']) => {
    if (!pricing || pricing.type === 'free') {
      return {
        label: 'Free',
        class: 'bg-green-500/20 text-green-400 border-green-500/30',
      };
    }
    const price = pricing.price || 0;
    const currency = pricing.currency || 'EUR';
    const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
    switch (pricing.type) {
      case 'one_time':
        return {
          label: `${symbol}${price}`,
          class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        };
      case 'subscription':
        return {
          label: `${symbol}${price}/mo`,
          class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
        };
      case 'per_run':
        return {
          label: `${symbol}${price}/run`,
          class: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
        };
      default:
        return {
          label: 'Free',
          class: 'bg-green-500/20 text-green-400 border-green-500/30',
        };
    }
  };

  const renderStars = (rating?: number) => {
    const stars = Math.round(rating || 0);
    return (
      <div class="flex items-center gap-0.5">
        <For each={Array(5).fill(0)}>
          {(_, i) => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill={i() < stars ? '#facc15' : 'none'}
              stroke="#facc15"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          )}
        </For>
      </div>
    );
  };

  const getItemUrl = (item: MarketplaceItem) => {
    switch (item.type) {
      case 'project':
        return `/projects/${item.id}`;
      case 'agent':
        return `/agents/${item.id}`;
      case 'skill':
        return `/skills/${item.id}`;
      case 'artifact':
        return `/artifacts/${item.id}`;
      default:
        return '#';
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
              Marketplace
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              Discover projects, agents, skills, and artifacts from the community
            </p>
          </div>
        </div>
      </div>

      {/* Featured Section */}
      <Show when={showFeatured() && !hasActiveFilters() && (featuredItems()?.length ?? 0) > 0}>
        <div class="mb-8">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-medium text-gray-200">Featured</h2>
            <button
              type="button"
              onClick={() => setShowFeatured(false)}
              class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Hide
            </button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <For each={(featuredItems() || []).slice(0, 4)}>
              {(item) => (
                <A
                  href={getItemUrl(item)}
                  class="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-lg border border-gray-700 hover:border-pink-500/50 p-4 transition-all group"
                >
                  <div class="flex items-center gap-3 mb-3">
                    {getTypeIcon(item.type)}
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${getTypeBadgeClass(
                        item.type,
                      )}`}
                    >
                      {item.type}
                    </span>
                  </div>
                  <h3 class="text-gray-200 font-medium group-hover:text-pink-400 transition-colors mb-2 line-clamp-1">
                    {item.name}
                  </h3>
                  <p class="text-gray-500 text-sm line-clamp-2 mb-3">
                    {item.description || 'No description'}
                  </p>
                  <div class="flex items-center justify-between">
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${
                        getPricingBadge(item.pricing).class
                      }`}
                    >
                      {getPricingBadge(item.pricing).label}
                    </span>
                    <div class="flex items-center gap-1">{renderStars(item.rating)}</div>
                  </div>
                </A>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* Filters and Controls */}
      <div class="mb-6 bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div class="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div class="flex-1 min-w-[200px]">
            <div class="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search marketplace..."
                value={searchQuery()}
                onInput={(e) => setSearchQuery(e.currentTarget.value)}
                class="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-pink-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <select
            value={selectedType()}
            onChange={(e) => setSelectedType(e.currentTarget.value)}
            class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-pink-500"
          >
            <option value="">All Types</option>
            <option value="project">Projects</option>
            <option value="agent">Agents</option>
            <option value="skill">Skills</option>
            <option value="artifact">Artifacts</option>
          </select>

          {/* Category Filter */}
          <select
            value={selectedCategory()}
            onChange={(e) => setSelectedCategory(e.currentTarget.value)}
            class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-pink-500"
          >
            <option value="">All Categories</option>
            <option value="productivity">Productivity</option>
            <option value="automation">Automation</option>
            <option value="data">Data & Analytics</option>
            <option value="creative">Creative</option>
            <option value="development">Development</option>
            <option value="business">Business</option>
          </select>

          {/* Rating Filter */}
          <select
            value={minRating() ?? ''}
            onChange={(e) =>
              setMinRating(e.currentTarget.value ? Number(e.currentTarget.value) : undefined)
            }
            class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-pink-500"
          >
            <option value="">Any Rating</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
          </select>

          {/* View Mode Toggle */}
          <div class="flex rounded-lg overflow-hidden border border-gray-600">
            <button
              type="button"
              onClick={() => setViewMode('card')}
              class={`px-3 py-2 ${
                viewMode() === 'card'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-900/50 text-gray-400 hover:text-gray-200'
              }`}
              title="Card View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect width="7" height="7" x="3" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="14" rx="1" />
                <rect width="7" height="7" x="3" y="14" rx="1" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              class={`px-3 py-2 ${
                viewMode() === 'table'
                  ? 'bg-pink-600 text-white'
                  : 'bg-gray-900/50 text-gray-400 hover:text-gray-200'
              }`}
              title="Table View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          {/* Clear Filters */}
          <Show when={hasActiveFilters()}>
            <button
              type="button"
              onClick={clearFilters}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
            >
              Clear Filters
            </button>
          </Show>
        </div>
      </div>

      {/* Results Count and Page Size */}
      <div class="mb-4 flex items-center justify-between text-sm text-gray-400">
        <span>
          {totalCount()} item{totalCount() !== 1 ? 's' : ''} found
        </span>
        <div class="flex items-center gap-2">
          <span>Show:</span>
          <select
            value={pageSize()}
            onChange={(e) => setPageSize(Number(e.currentTarget.value) as PageSize)}
            class="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 focus:outline-none focus:border-pink-500"
          >
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      <Show when={marketplaceData.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!marketplaceData.loading && items().length === 0}>
        <div class="text-center py-12">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mx-auto text-gray-600 mb-4"
          >
            <circle cx="8" cy="21" r="1" />
            <circle cx="19" cy="21" r="1" />
            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
          </svg>
          <h3 class="text-xl font-medium text-gray-300 mb-2">No items found</h3>
          <p class="text-gray-500 mb-6">
            {hasActiveFilters()
              ? 'Try adjusting your filters'
              : 'No marketplace items available yet'}
          </p>
        </div>
      </Show>

      {/* Card View */}
      <Show when={!marketplaceData.loading && viewMode() === 'card' && items().length > 0}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <For each={items()}>
            {(item) => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-all">
                <div class="p-4">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center gap-3">
                      {getTypeIcon(item.type)}
                      <div class="flex-1 min-w-0">
                        <A
                          href={getItemUrl(item)}
                          class="text-lg font-medium text-gray-200 hover:text-pink-400 transition-colors line-clamp-1"
                        >
                          {item.name}
                        </A>
                      </div>
                    </div>
                  </div>

                  <p class="text-gray-500 text-sm mb-3 line-clamp-2">
                    {item.description || 'No description'}
                  </p>

                  <div class="flex flex-wrap gap-2 mb-3">
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${getTypeBadgeClass(
                        item.type,
                      )}`}
                    >
                      {item.type}
                    </span>
                    <span
                      class={`px-2 py-0.5 rounded-full text-xs border ${
                        getPricingBadge(item.pricing).class
                      }`}
                    >
                      {getPricingBadge(item.pricing).label}
                    </span>
                  </div>

                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      {renderStars(item.rating)}
                      <span class="text-xs text-gray-500">({item.review_count || 0})</span>
                    </div>
                    <span class="text-xs text-gray-500">{item.usage_count || 0} uses</span>
                  </div>
                </div>

                <div class="border-t border-gray-700 px-4 py-2 flex items-center justify-between">
                  <Show when={item.owner_name}>
                    <span class="text-xs text-gray-500">by {item.owner_name}</span>
                  </Show>
                  <div class="flex items-center gap-2 ml-auto">
                    <A
                      href={getItemUrl(item)}
                      class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                    >
                      View
                    </A>
                    <button
                      type="button"
                      class="text-sm text-pink-400 hover:text-pink-300 transition-colors"
                    >
                      {item.pricing?.type === 'free' ? 'Get' : 'Buy'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Table View */}
      <Show when={!marketplaceData.loading && viewMode() === 'table' && items().length > 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
          <table class="w-full">
            <thead class="bg-gray-900/50 border-b border-gray-700">
              <tr>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Price</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Rating</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Uses</th>
                <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Creator</th>
                <th class="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-700">
              <For each={items()}>
                {(item) => (
                  <tr class="hover:bg-gray-800/50 transition-colors">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-3">
                        {getTypeIcon(item.type)}
                        <div>
                          <A
                            href={getItemUrl(item)}
                            class="text-gray-200 hover:text-pink-400 transition-colors font-medium"
                          >
                            {item.name}
                          </A>
                          <p class="text-gray-500 text-xs mt-0.5 line-clamp-1">
                            {item.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getTypeBadgeClass(
                          item.type,
                        )}`}
                      >
                        {item.type}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${
                          getPricingBadge(item.pricing).class
                        }`}
                      >
                        {getPricingBadge(item.pricing).label}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-1">
                        {renderStars(item.rating)}
                        <span class="text-xs text-gray-500 ml-1">({item.review_count || 0})</span>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-400">{item.usage_count || 0}</td>
                    <td class="px-4 py-3 text-sm text-gray-500">{item.owner_name || 'Unknown'}</td>
                    <td class="px-4 py-3 text-right">
                      <div class="flex items-center justify-end gap-2">
                        <A
                          href={getItemUrl(item)}
                          class="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          View
                        </A>
                        <button
                          type="button"
                          class="text-sm text-pink-400 hover:text-pink-300 transition-colors"
                        >
                          {item.pricing?.type === 'free' ? 'Get' : 'Buy'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* Pagination */}
      <Show when={totalPages() > 1}>
        <div class="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage() === 1}
            class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <For
            each={Array.from({ length: Math.min(5, totalPages()) }, (_, i) => {
              const page = currentPage();
              const total = totalPages();
              if (total <= 5) return i + 1;
              if (page <= 3) return i + 1;
              if (page >= total - 2) return total - 4 + i;
              return page - 2 + i;
            })}
          >
            {(page) => (
              <button
                type="button"
                onClick={() => setCurrentPage(page)}
                class={`px-3 py-2 rounded-lg border transition-colors ${
                  currentPage() === page
                    ? 'bg-pink-600 border-pink-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {page}
              </button>
            )}
          </For>

          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages(), p + 1))}
            disabled={currentPage() === totalPages()}
            class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </Show>
    </div>
  );
};

export default Marketplace;
